import { useState } from "react"
import type { MouseEvent } from "react"
import { useSelector } from "react-redux"
import { DateRange } from "react-date-range"
import type { Range, RangeFocus, RangeKeyDict } from "react-date-range"
import "react-date-range/dist/styles.css"
import "react-date-range/dist/theme/default.css"
import { da } from "date-fns/locale"
import type { DateTime } from "luxon"
import { MenuItem, MenuList, Popover, Stack, Typography, useTheme } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarDay, faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons"

import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { dateLocaleMap } from "../../../../../shared/components/table/StandardDateRangeFilter"
import type { DtDateRange } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { selectSelectedLocale } from "../../../../localization/localizationSelectors"
import { useDateTime } from "../../../../localization/useDateTime"
import type { DateTimeFactory } from "../../../../localization/useDateTime"

import { getPeriodRange } from "../upcomingMeetingsLib"
import { UPCOMING_MEETINGS_PRESETS, UpcomingMeetingsPeriod } from "../upcomingMeetingsTypes"

const SELECTION_KEY = "selection"

const useStyles = makeStyles()((theme) => ({
    trigger: {
        height: "3rem",
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[900],
        backgroundColor: theme.palette.common.white,
        "& .MuiButton-startIcon": {
            marginLeft: 0,
            marginRight: ".5rem",
        },
        "& .MuiButton-endIcon": {
            marginLeft: ".5rem",
            marginRight: 0,
        },
        "& .MuiButton-startIcon > *:nth-of-type(1)": {
            fontSize: ".875rem",
        },
        "& .MuiButton-endIcon > *:nth-of-type(1)": {
            fontSize: ".75rem",
        },
    },
    triggerIcon: {
        color: theme.palette.tailwind.slate[500],
    },
    presets: {
        minWidth: "11.25rem",
        padding: ".5rem",
        backgroundColor: theme.palette.tailwind.slate[50],
        borderRight: `1px solid ${theme.palette.tailwind.slate[100]}`,
    },
    preset: {
        borderRadius: ".5rem",
        fontWeight: 600,
    },
    presetCheck: {
        color: theme.palette.primary.main,
    },
    presetCheckHidden: {
        visibility: "hidden",
    },
    calendar: {
        padding: ".875rem 1rem",
    },
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
    footer: {
        borderTop: `1px solid ${theme.palette.tailwind.slate[100]}`,
        paddingTop: ".75rem",
    },
    selection: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[500],
    },
}))

type Props = Readonly<{
    period: UpcomingMeetingsPeriod
    customRange: DtDateRange
    onPresetSelect: (period: UpcomingMeetingsPeriod) => void
    onCustomRangeApply: (range: DtDateRange) => void
}>

function formatRange(dateTimeFactory: DateTimeFactory, start: DateTime, end: DateTime) {
    if (start.hasSame(end, "day")) return dateTimeFactory.formatMonthDay(start)
    return `${dateTimeFactory.formatMonthDay(start)} – ${dateTimeFactory.formatMonthDay(end)}`
}

export function UpcomingMeetingsDateFilter({ period, customRange, onPresetSelect, onCustomRangeApply }: Props) {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const dateTimeFactory = useDateTime()
    const selectedLocale = useSelector(selectSelectedLocale)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [draft, setDraft] = useState<Range>({ key: SELECTION_KEY })
    const [focusedRange, setFocusedRange] = useState<RangeFocus>([0, 0])

    const toDateTime = (date: Date | undefined) =>
        date ? dateTimeFactory.fromJSDate(date).startOf("day") : dateTimeFactory.now().startOf("day")

    const draftStart = toDateTime(draft.startDate)
    const draftEnd = toDateTime(draft.endDate)
    const isPickingEnd = focusedRange[1] === 1

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        const today = dateTimeFactory.now().startOf("day")
        const range = getPeriodRange(period, customRange, dateTimeFactory.now()) ?? { startDate: today, endDate: today }

        setDraft({ key: SELECTION_KEY, startDate: range.startDate.toJSDate(), endDate: range.endDate.toJSDate() })
        setFocusedRange([0, 0])
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => setAnchorEl(null)

    const handlePresetSelect = (preset: UpcomingMeetingsPeriod) => {
        onPresetSelect(preset)
        handleClose()
    }

    const handleApply = () => {
        onCustomRangeApply({ startDate: draftStart, endDate: draftEnd })
        handleClose()
    }

    const triggerLabel =
        period === UpcomingMeetingsPeriod.CUSTOM ? (
            <>{formatRange(dateTimeFactory, customRange.startDate, customRange.endDate)}</>
        ) : (
            <LocalizedEnum base="service-center-reports-upcoming-meetings-period" enumValue={period}>
                <>{period}</>
            </LocalizedEnum>
        )

    return (
        <>
            <StandardButton
                variant="outlined"
                color="grey"
                textTransform="none"
                fullWidth={false}
                size="medium"
                className={classes.trigger}
                startIcon={<FontAwesomeIcon icon={faCalendarDay} className={classes.triggerIcon} />}
                endIcon={<FontAwesomeIcon icon={faChevronDown} className={classes.triggerIcon} />}
                onClick={handleOpen}
            >
                {triggerLabel}
            </StandardButton>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Stack direction={{ xs: "column", sm: "row" }}>
                    <MenuList className={classes.presets}>
                        {UPCOMING_MEETINGS_PRESETS.map((preset) => (
                            <MenuItem
                                key={preset}
                                selected={preset === period}
                                className={classes.preset}
                                onClick={() => handlePresetSelect(preset)}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    gap="1rem"
                                    flex={1}
                                >
                                    <LocalizedEnum
                                        base="service-center-reports-upcoming-meetings-period"
                                        enumValue={preset}
                                    >
                                        <span>{preset}</span>
                                    </LocalizedEnum>
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        className={cx(
                                            classes.presetCheck,
                                            preset !== period && classes.presetCheckHidden,
                                        )}
                                    />
                                </Stack>
                            </MenuItem>
                        ))}
                    </MenuList>

                    <Stack gap=".625rem" className={classes.calendar}>
                        {isPickingEnd ? (
                            <LocalizedStrict id="service-center-reports-upcoming-meetings-date-filter-hint-end">
                                <Typography variant="caption" className={classes.hint}>
                                    Now pick the end date in the calendar
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict id="service-center-reports-upcoming-meetings-date-filter-hint-start">
                                <Typography variant="caption" className={classes.hint}>
                                    Pick a start date in the calendar
                                </Typography>
                            </LocalizedStrict>
                        )}
                        <DateRange
                            ranges={[draft]}
                            onChange={(ranges: RangeKeyDict) => setDraft(ranges[SELECTION_KEY])}
                            focusedRange={focusedRange}
                            onRangeFocusChange={setFocusedRange}
                            moveRangeOnFirstSelection={false}
                            months={1}
                            rangeColors={[theme.palette.primary.main]}
                            locale={dateLocaleMap[selectedLocale] ?? da}
                        />
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap=".625rem"
                            className={classes.footer}
                        >
                            <Typography variant="body2" className={classes.selection}>
                                {formatRange(dateTimeFactory, draftStart, draftEnd)}
                            </Typography>
                            <LocalizedStrict id="service-center-reports-upcoming-meetings-date-filter-apply-button">
                                <StandardButton color="black" size="small" fullWidth={false} onClick={handleApply}>
                                    Apply
                                </StandardButton>
                            </LocalizedStrict>
                        </Stack>
                    </Stack>
                </Stack>
            </Popover>
        </>
    )
}
